import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fragment, type ReactNode } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { useStructuredObservationDialog } from '../StructuredObservationList.context'

type StructuredObservationItemDialogueProps = {
  title: string | ((mode: 'insert' | 'update') => string)
  /** Steps back to the screen the dialog came from. Omitted when there is nothing to step back to. */
  back?: { label: string; onBack: () => void }
  children: ReactNode
}

export const StructuredObservationItemDialogue = ({
  title,
  back,
  children,
}: StructuredObservationItemDialogueProps) => {
  const theme = useTheme()
  const { dialog, closeDialog } = useStructuredObservationDialog()

  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()

  const isOpen = dialog.mode !== 'closed'
  const dialogKey = dialog.mode === 'update' ? `${dialog.mode}:${dialog.itemId}` : dialog.mode
  const resolvedTitle = typeof title === 'function' ? title(dialog.mode === 'closed' ? 'insert' : dialog.mode) : title

  // A back button makes this a sub screen, so its title centres between the
  // button and the spacer that balances it. A screen without one reads as the
  // dialog's own title and stays where every other dialog title sits.
  const heading = back ? (
    <StHeading>
      <IconButton
        type='button'
        size='large'
        variant='Tertiary'
        icon={['far', 'chevron-left']}
        aria-label={back.label}
        onClick={back.onBack}
      />

      <StCenteredTitle noMargin textAlign='center'>
        {resolvedTitle}
      </StCenteredTitle>

      <StBackButtonSpacer />
    </StHeading>
  ) : (
    <H2 noMargin>{resolvedTitle}</H2>
  )

  if (isMobile) {
    return (
      <BottomSheet steps={[100]} visible={isOpen} onClose={closeDialog}>
        <Padding
          px={theme.UI.Spacing.Space4}
          pb={theme.UI.Spacing.Space6}
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.UI.SpacingPx.Space4,
          }}
        >
          {heading}
          <StSheetBody key={dialogKey}>{children}</StSheetBody>
        </Padding>
      </BottomSheet>
    )
  }

  return (
    <Modal isVisible={isOpen} onClose={closeDialog} maxHeight='90vh'>
      <StModalContent>
        <FlexRow justifyContent='space-between' alignItems='center'>
          {heading}
          <FontAwesomeIcon
            icon={['far', 'close']}
            fontSize={20}
            color={theme.Colors.Neutrals.Darker}
            onClick={closeDialog}
            style={{ cursor: 'pointer' }}
          />
        </FlexRow>
        <Fragment key={dialogKey}>{children}</Fragment>
      </StModalContent>
    </Modal>
  )
}

// ================================ Styled Components ================================

/** Width of a large IconButton, which the back button is. */
const BACK_BUTTON_SIZE_PX = 48

const StHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  width: 100%;
`

const StCenteredTitle = styled(H2)`
  flex: 1;
  min-width: 0;
`

/* Balances the back button, so the title between them sits in the middle. */
const StBackButtonSpacer = styled.div`
  flex-shrink: 0;
  width: ${BACK_BUTTON_SIZE_PX}px;
`

const StSheetBody = styled.div`
  flex: 1;
  min-height: 0;
`

const StModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`
